import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Download, Calendar, Image, X, ChevronLeft, ChevronRight, Camera, RotateCw, RotateCcw } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.4 } }),
};

export default function ClientGallery() {
  const { galleryId } = useParams();
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [lightboxRotation, setLightboxRotation] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const res = await axios.get(`${API}/client/galleries/${galleryId}`, { withCredentials: true });
        setGallery(res.data);
      } catch {
        toast.error("Unable to load gallery.");
        navigate("/vip/dashboard");
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, [galleryId, navigate]);

  const openLightbox = (idx) => { setLightbox(idx); setLightboxRotation(0); };
  const closeLightbox = () => { setLightbox(null); setLightboxRotation(0); };
  const nextImage = () => {
    if (!gallery?.media) return;
    setLightboxRotation(0);
    setLightbox((prev) => (prev + 1) % gallery.media.length);
  };
  const prevImage = () => {
    if (!gallery?.media) return;
    setLightboxRotation(0);
    setLightbox((prev) => (prev - 1 + gallery.media.length) % gallery.media.length);
  };

  const downloadImage = (url, filename) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen bg-luxury-black flex items-center justify-center">
        <div className="text-gold font-body animate-pulse">Loading gallery...</div>
      </div>
    );
  }

  if (!gallery) return null;

  const media = gallery.media || [];

  return (
    <div data-testid="client-gallery-page" className="pt-20 min-h-screen bg-luxury-black">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Back Link */}
        <Link to="/vip/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-gold text-sm font-body mb-8 transition-colors" data-testid="gallery-back-link">
          <ArrowLeft size={16} /> Back to Dashboard
        </Link>

        {/* Gallery Header */}
        <motion.div initial="hidden" animate="visible" className="mb-10">
          <motion.div variants={fadeUp} custom={0} className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-gold/10 flex items-center justify-center">
              <Camera size={18} className="text-gold" />
            </div>
            <span className="text-xs font-body uppercase tracking-[0.2em] text-gold">{gallery.event_type}</span>
          </motion.div>
          <motion.h1 variants={fadeUp} custom={1} className="font-heading text-3xl sm:text-4xl font-light text-white mb-3" data-testid="gallery-title">
            {gallery.title}
          </motion.h1>
          {gallery.description && (
            <motion.p variants={fadeUp} custom={2} className="text-white/50 font-body font-light text-sm leading-relaxed max-w-2xl mb-4">
              {gallery.description}
            </motion.p>
          )}
          <motion.div variants={fadeUp} custom={3} className="flex items-center gap-6 text-white/30 text-xs font-body">
            <span className="flex items-center gap-1"><Calendar size={13} /> {gallery.event_date}</span>
            <span className="flex items-center gap-1"><Image size={13} /> {media.length} items</span>
          </motion.div>
        </motion.div>

        {/* Media Grid */}
        {media.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Image size={48} className="text-gold/30 mx-auto mb-4" />
            <p className="text-white/40 font-body text-sm">Media is being prepared. Check back soon.</p>
          </div>
        ) : (
          <motion.div initial="hidden" animate="visible" className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1 sm:gap-1.5">
            {media.map((item, i) => (
              <motion.div
                key={item.id}
                variants={fadeUp}
                custom={i}
                className="relative group cursor-pointer overflow-hidden aspect-square bg-luxury-surface"
                data-testid={`media-item-${item.id}`}
              >
                <img
                  src={item.url}
                  alt={item.caption || item.filename}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onClick={() => openLightbox(i)}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-200 pointer-events-none" />
                {/* Download button */}
                <button
                  onClick={(e) => { e.stopPropagation(); downloadImage(item.url, item.filename); }}
                  data-testid={`download-${item.id}`}
                  className="absolute top-2 right-2 w-8 h-8 bg-black/60 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gold/80 hover:text-black text-white/80"
                >
                  <Download size={12} />
                </button>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Download All */}
        {media.length > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-8 text-center">
            <p className="text-white/30 text-xs font-body">Hover over images and click the download icon to save individual files.</p>
          </motion.div>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && media[lightbox] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center"
            onClick={closeLightbox}
            data-testid="gallery-lightbox"
          >
            <button onClick={(e) => { e.stopPropagation(); closeLightbox(); }} className="absolute top-6 right-6 text-white/60 hover:text-white z-10" data-testid="gallery-lightbox-close">
              <X size={28} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); prevImage(); }} className="absolute left-6 top-1/2 -translate-y-1/2 text-white/60 hover:text-white z-10">
              <ChevronLeft size={36} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); nextImage(); }} className="absolute right-6 top-1/2 -translate-y-1/2 text-white/60 hover:text-white z-10">
              <ChevronRight size={36} />
            </button>
            {/* Rotate controls — visual fix for sideways legacy photos */}
            <div onClick={(e) => e.stopPropagation()} className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
              <button data-testid="client-lightbox-rotate-left" onClick={() => setLightboxRotation((r) => (r - 90) % 360)} className="flex items-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-gold/30 border border-white/15 text-white text-xs font-body uppercase tracking-wider transition-colors" title="Rotate left 90°"><RotateCcw size={14} /> Left</button>
              <button data-testid="client-lightbox-rotate-right" onClick={() => setLightboxRotation((r) => (r + 90) % 360)} className="flex items-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-gold/30 border border-white/15 text-white text-xs font-body uppercase tracking-wider transition-colors" title="Rotate right 90°"><RotateCw size={14} /> Right</button>
              {lightboxRotation !== 0 && (
                <button data-testid="client-lightbox-rotate-reset" onClick={() => setLightboxRotation(0)} className="px-3 py-2 bg-gold/15 hover:bg-gold/25 border border-gold/40 text-gold text-xs font-body uppercase tracking-wider transition-colors">Reset</button>
              )}
            </div>
            <div onClick={(e) => e.stopPropagation()} className="max-w-5xl max-h-[85vh] px-4">
              <img
                data-testid="client-lightbox-image"
                src={media[lightbox].url}
                alt={media[lightbox].caption || media[lightbox].filename}
                style={{ transform: `rotate(${lightboxRotation}deg)` }}
                className="max-w-full max-h-[78vh] object-contain transition-transform duration-300"
              />
              <div className="flex items-center justify-between mt-4 px-2">
                <div>
                  <p className="text-white text-sm font-body font-light">{media[lightbox].caption || media[lightbox].filename}</p>
                  <p className="text-white/30 text-xs font-body">{lightbox + 1} of {media.length}</p>
                </div>
                <button
                  onClick={() => downloadImage(media[lightbox].url, media[lightbox].filename)}
                  className="flex items-center gap-2 bg-gold/20 text-gold px-4 py-2 text-xs font-body hover:bg-gold/30 transition-colors"
                >
                  <Download size={14} /> Download
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
